import { fieldRows, fineRows, fineSeeds, FIELD_H, type FieldResult } from "./plateField";

/**
 * Runs the heavy per-ball precomputation off the touch path.
 * - Preferred: Web Workers (separate cores; the page never waits on them). A crack field is split into bands of rows,
 *   one per worker, and stitched back together: the big ball's 4096×2048 field takes ~2 s on one core.
 * - Fallback (worker unavailable): the main thread computes in idle slices (requestIdleCallback,
 *   setTimeout where Safari lacks it), a few rows at a time, so frames keep their budget.
 * Jobs run one at a time, highest priority first; bump() moves a ball the finger is already on to the front.
 */

type Job = {
  id: number;
  kind: "field" | "detail";
  priority: number;
  payload: { seeds?: Float32Array; fineSeed?: number; fineCount?: number; res?: number; seed?: number; w?: number; h?: number };
  resolve: (r: unknown) => void;
  reject: (e: unknown) => void;
};

type IdleDeadline = { timeRemaining(): number };
const ric = (cb: (d: IdleDeadline) => void) => {
  const w = window as Window & { requestIdleCallback?: (cb: (d: IdleDeadline) => void, o?: { timeout: number }) => number };
  if (typeof w.requestIdleCallback === "function") w.requestIdleCallback(cb, { timeout: 120 });
  else setTimeout(() => { const t0 = performance.now(); cb({ timeRemaining: () => Math.max(0, 6 - (performance.now() - t0)) }); }, 16);
};

/** Runs `fn` when the main thread is idle (requestIdleCallback; setTimeout on Safari). */
export function onIdle(fn: () => void) { ric(() => fn()); }

export type FieldTicket = { promise: Promise<FieldResult>; bump(): void; cancel(): void };

export class FieldPool {
  private workers: Worker[] = [];
  private workerFailed = false;
  private queue: Job[] = [];
  private running: Job | null = null;
  /** the running job's parts still out, and the ones back (by band) */
  private parts: { left: number; got: unknown[] } | null = null;
  private nextId = 1;
  private disposed = false;

  constructor() {
    // one core is left to the page (an iPhone 13 has 6: four of them get a worker)
    const n = Math.max(1, Math.min(4, (navigator.hardwareConcurrency || 2) - 1));
    try {
      for (let i = 0; i < n; i++) {
        const w = new Worker(new URL("./plateField.worker.ts", import.meta.url), { type: "module" });
        w.onmessage = (e: MessageEvent<{ id: number; part?: number; ok: boolean; result?: unknown; error?: string }>) => this.onDone(e.data);
        w.onerror = () => this.failWorker();
        this.workers.push(w);
      }
    } catch {
      if (!this.workers.length) this.workerFailed = true;
    }
  }

  /**
   * The crack field of plate `seeds`, plus (fineCount > 0) the finer crack layer of `fineCount` cells from `fineSeed`,
   * at texture scale `res` (1 = 1024×512, 2 = 2048×1024, 4 = 4096×2048).
   */
  field(seeds: Float32Array, priority = 0, fineSeed = 1, fineCount = 0, res = 1): FieldTicket {
    let job!: Job;
    const promise = new Promise<FieldResult>((resolve, reject) => {
      job = { id: this.nextId++, kind: "field", priority, payload: { seeds, fineSeed, fineCount, res }, resolve: resolve as (r: unknown) => void, reject };
    });
    this.enqueue(job);
    return {
      promise,
      bump: () => { if (job.priority < 10) { job.priority = 10; this.queue.sort((a, b) => b.priority - a.priority); } },
      cancel: () => { const i = this.queue.indexOf(job); if (i >= 0) { this.queue.splice(i, 1); job.reject(new Error("cancelled")); } },
    };
  }

  detail(seed: number, w: number, h: number): Promise<Uint8Array> {
    return new Promise<Uint8Array>((resolve, reject) => {
      this.enqueue({ id: this.nextId++, kind: "detail", priority: -1, payload: { seed, w, h }, resolve: resolve as (r: unknown) => void, reject });
    });
  }

  dispose() {
    this.disposed = true;
    for (const w of this.workers) w.terminate();
    this.workers = [];
    for (const j of this.queue) j.reject(new Error("disposed"));
    this.queue = [];
  }

  private enqueue(job: Job) {
    if (this.disposed) { job.reject(new Error("disposed")); return; }
    this.queue.push(job);
    this.queue.sort((a, b) => b.priority - a.priority);
    this.pump();
  }

  private pump() {
    if (this.running || !this.queue.length || this.disposed) return;
    const job = this.queue.shift()!;
    this.running = job;
    if (this.workers.length && !this.workerFailed) {
      if (job.kind === "field") {
        // a band of rows per worker; each gets a copy of the seeds (nothing the ball still needs is transferred away)
        const p = job.payload, H = FIELD_H * (p.res ?? 1), nb = this.workers.length;
        this.parts = { left: nb, got: new Array(nb) };
        for (let b = 0; b < nb; b++) {
          const y0 = Math.round((H * b) / nb), y1 = Math.round((H * (b + 1)) / nb);
          this.workers[b].postMessage({ id: job.id, part: b, kind: "field", seeds: p.seeds!.slice(), fineSeed: p.fineSeed, fineCount: p.fineCount, res: p.res, y0, y1 });
        }
      } else {
        this.parts = { left: 1, got: [null] };
        this.workers[0].postMessage({ id: job.id, part: 0, kind: "detail", ...job.payload });
      }
    } else {
      this.runOnMainThread(job);
    }
  }

  private onDone(msg: { id: number; part?: number; ok: boolean; result?: unknown; error?: string }) {
    const job = this.running, parts = this.parts;
    if (!job || job.id !== msg.id || !parts) return;
    if (!msg.ok) {
      this.running = null; this.parts = null;
      job.reject(new Error(msg.error));
      this.pump();
      return;
    }
    parts.got[msg.part ?? 0] = msg.result;
    if (--parts.left > 0) return;
    this.running = null; this.parts = null;
    // stitching a big field is a few tens of MB of copying: at idle, never on a touch
    if (job.kind === "field") onIdle(() => { if (!this.disposed) job.resolve(stitch(parts.got as FieldResult[])); });
    else job.resolve(parts.got[0]);
    this.pump();
  }

  private failWorker() {
    // a worker that cannot load (old browser, CSP): redo the current job and the rest on the main thread
    this.workerFailed = true;
    for (const w of this.workers) w.terminate();
    this.workers = [];
    const job = this.running;
    this.running = null; this.parts = null;
    if (job) this.queue.unshift(job);
    this.pump();
  }

  /** Main-thread fallback: a few rows per idle slice so no frame loses more than a few ms. */
  private runOnMainThread(job: Job) {
    if (job.kind === "detail") {
      // ~50–400 ms in one go: not worth a main-thread stall; the flat placeholder stays (cosmetic only)
      job.reject(new Error("detail map skipped without a worker"));
      this.running = null; this.pump();
      return;
    }
    // the plates first, then (if asked) the finer layer, a few rows per slice
    let y = 0;
    let res: FieldResult | undefined;
    const nf = job.payload.fineCount ?? 0, fs = nf ? fineSeeds(job.payload.fineSeed ?? 1, nf) : null;
    const k = job.payload.res ?? 1, H = FIELD_H * k, rows = Math.max(2, 8 / (k * k)) | 0; // same work per slice at any size
    const total = fs ? H * 2 : H;
    const slice = (d: IdleDeadline) => {
      if (this.disposed) return;
      do {
        if (y < H) { const y1 = Math.min(H, y + rows); res = fieldRows(job.payload.seeds!, k, y, y1, res); y = y1; }
        else { const f0 = y - H, f1 = Math.min(H, f0 + rows); res!.fine = fineRows(fs!, k, f0, f1, res!.fine); y = H + f1; }
      } while (y < total && d.timeRemaining() > 3);
      if (y < total) { ric(slice); return; }
      this.running = null;
      job.resolve(res);
      this.pump();
    };
    ric(slice);
  }
}

/** The workers' bands (in row order) as one field: rows end to end, plate areas summed. */
function stitch(bands: FieldResult[]): FieldResult {
  if (bands.length === 1) return bands[0];
  const W = bands[0].w, H = bands[0].h, n = W * H, na = bands[0].area.length;
  const ids = new Uint8Array(n * 2), dist = new Uint8Array(n), area = new Float32Array(na);
  const fine = bands[0].fine ? new Uint8Array(n * 2) : undefined;
  let o = 0, ms = 0;
  for (const b of bands) {
    ids.set(b.ids, o * 2); dist.set(b.dist, o);
    if (fine && b.fine) fine.set(b.fine, o * 2);
    for (let i = 0; i < na; i++) area[i] += b.area[i];
    o += b.dist.length; ms = Math.max(ms, b.ms);
  }
  return { ids, dist, area, fine, w: W, h: H, ms };
}
